import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["minInput", "maxInput", "minLabel", "maxLabel", "rangeMin", "rangeMax", "track", "activeTrack", "histogram"]
    static values = {
        min: Number,
        max: Number,
        currentMin: Number,
        currentMax: Number
    }

    connect() {
        this.initializeValues()
        this.updateSliderPositions()
        this.updateLabels()
        this.updateHistogramColors()

        // Listen for turbo:frame-load events
        document.addEventListener('turbo:frame-load', this.handleTurboFrameLoad.bind(this))

        // Listen for clear-filters event
        document.addEventListener('clear-filters', this.resetPriceRange.bind(this))
    }

    disconnect() {
        // Clean up event listeners when controller disconnects
        document.removeEventListener('turbo:frame-load', this.handleTurboFrameLoad.bind(this))
        document.removeEventListener('clear-filters', this.resetPriceRange.bind(this))
    }

    // Reset price range to default values
    resetPriceRange() {
        // Reset to min/max values
        this.currentMinValue = this.minValue || 0
        this.currentMaxValue = this.maxValue || 100

        // Update input values
        this.minInputTarget.value = ''
        this.maxInputTarget.value = ''

        // Update UI
        this.updateSliderPositions()
        this.updateLabels()
        this.updateHistogramColors()
    }

    handleTurboFrameLoad(event) {
        this.updateHistogramColors()
    }

    initializeValues() {
        // Set default values if not provided
        if (!this.hasCurrentMinValue) this.currentMinValue = this.minValue || 0
        if (!this.hasCurrentMaxValue) this.currentMaxValue = this.maxValue || 100

        // Set input values
        this.minInputTarget.value = this.currentMinValue
        this.maxInputTarget.value = this.currentMaxValue
    }

    // Update histogram colors based on the current range
    updateHistogramColors() {
        if (!this.hasHistogramTarget) {
            return
        }

        // Get all bars in the histogram
        const bars = this.histogramTarget.querySelectorAll('.histogram-bar')

        bars.forEach(bar => {
            const minPrice = parseFloat(bar.dataset.priceMin)
            const maxPrice = parseFloat(bar.dataset.priceMax)

            // Check if price range is completely contained within selected range
            const isInRange = minPrice >= this.currentMinValue && maxPrice <= this.currentMaxValue

            // Apply appropriate styling
            if (isInRange) {
                bar.classList.remove('bg-warm-gray-300')
                bar.classList.add('bg-indigo-500')
                bar.style.backgroundColor = 'rgb(99, 102, 241)' // Match indigo-500
            } else {
                bar.classList.remove('bg-indigo-500')
                bar.classList.add('bg-warm-gray-300')
                bar.style.backgroundColor = 'rgb(214, 211, 209)' // Match warm-gray-300
            }
        })
    }

    updateSliderPositions() {
        const range = this.maxValue - this.minValue
        const minPercent = ((this.currentMinValue - this.minValue) / range) * 100
        const maxPercent = ((this.currentMaxValue - this.minValue) / range) * 100

        // Update range slider positions
        this.rangeMinTarget.style.left = `${minPercent}%`
        this.rangeMaxTarget.style.left = `${maxPercent}%`

        // Update active track
        this.activeTrackTarget.style.left = `${minPercent}%`
        this.activeTrackTarget.style.width = `${maxPercent - minPercent}%`

        // Update histogram colors when slider positions change
        this.updateHistogramColors()
    }

    updateLabels() {
        this.minLabelTarget.textContent = `$${this.currentMinValue}`

        // Show "+" sign for max value if it's at the maximum
        const maxLabel = this.currentMaxValue >= this.maxValue ?
            `$${this.maxValue}+` :
            `$${this.currentMaxValue}`

        this.maxLabelTarget.textContent = maxLabel
    }

    onMinChange(event) {
        let newValue = parseInt(event.target.value, 10)

        // Ensure min doesn't exceed max
        if (newValue > this.currentMaxValue) {
            newValue = this.currentMaxValue
            this.minInputTarget.value = newValue
        }

        this.currentMinValue = newValue
        this.updateSliderPositions()
        this.updateLabels()
        this.notifyInputChange()
    }

    onMaxChange(event) {
        let newValue = parseInt(event.target.value, 10)

        // Ensure max doesn't go below min
        if (newValue < this.currentMinValue) {
            newValue = this.currentMinValue
            this.maxInputTarget.value = newValue
        }

        this.currentMaxValue = newValue
        this.updateSliderPositions()
        this.updateLabels()
        this.notifyInputChange()
    }

    dragStart(event) {
        // Determine which handle is being dragged
        const handle = event.currentTarget
        const isMin = handle === this.rangeMinTarget

        // Store initial position
        const startX = event.clientX || (event.touches && event.touches[0].clientX)
        const startValue = isMin ? this.currentMinValue : this.currentMaxValue

        // Create move and end handlers
        const moveHandler = (moveEvent) => this.dragMove(moveEvent, isMin, startX, startValue)
        const endHandler = () => this.dragEnd(moveHandler, endHandler)

        // Add event listeners for drag
        document.addEventListener('mousemove', moveHandler)
        document.addEventListener('mouseup', endHandler)
        document.addEventListener('touchmove', moveHandler)
        document.addEventListener('touchend', endHandler)

        // Prevent default to avoid text selection during drag
        event.preventDefault()
    }

    dragMove(event, isMin, startX, startValue) {
        // Get current position
        const currentX = event.clientX || (event.touches && event.touches[0].clientX) || startX
        const deltaX = currentX - startX

        // Calculate track width and value per pixel
        const trackWidth = this.trackTarget.offsetWidth
        const valuePerPixel = (this.maxValue - this.minValue) / trackWidth

        // Calculate new value based on drag distance
        let newValue = Math.round(startValue + (deltaX * valuePerPixel))

        // Constrain to min/max values
        newValue = Math.max(this.minValue, Math.min(this.maxValue, newValue))

        // Constrain min/max relative to each other
        if (isMin) {
            newValue = Math.min(newValue, this.currentMaxValue)
            this.currentMinValue = newValue
            this.minInputTarget.value = newValue
        } else {
            newValue = Math.max(newValue, this.currentMinValue)
            this.currentMaxValue = newValue
            this.maxInputTarget.value = newValue
        }

        // Update UI
        this.updateSliderPositions()
        this.updateLabels()
    }

    dragEnd(moveHandler, endHandler) {
        // Remove event listeners
        document.removeEventListener('mousemove', moveHandler)
        document.removeEventListener('mouseup', endHandler)
        document.removeEventListener('touchmove', moveHandler)
        document.removeEventListener('touchend', endHandler)

        // Update input values if they're at min/max boundary
        this.updateInputValues()

        // Notify about the change
        this.notifyInputChange()
    }

    updateInputValues() {
        // Clear min input if it's at the minimum value
        if (this.currentMinValue === this.minValue) {
            this.minInputTarget.value = ''
        }

        // Clear max input if it's at the maximum value
        if (this.currentMaxValue === this.maxValue) {
            this.maxInputTarget.value = ''
        }
    }

    notifyInputChange() {
        // Dispatch a custom event that ProductFiltersController can listen for
        this.element.dispatchEvent(new CustomEvent('price-range:change', {
            bubbles: true,
            detail: {
                minValue: this.currentMinValue === this.minValue ? '' : this.currentMinValue,
                maxValue: this.currentMaxValue === this.maxValue ? '' : this.currentMaxValue
            }
        }))
    }
}
